import {
  ColumnDataType,
  Provider,
  WorkflowComponentParamType,
  WorkflowComponentCodeParams
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

const M_TO_KM = 0.001
const M_TO_MILES = 0.000621371

class SpatialInfo extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'alteryx.spatialinfo',
        group: 'Spatial Operations',
        title: 'Spatial Info',
        description: `This component extracts tabular information about the spatial object.`,
        tags: ['alteryx']
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'field',
          type: WorkflowComponentParamType.Column,
          title: 'Spatial Object Field',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'areasqkm',
          type: WorkflowComponentParamType.Boolean,
          title: 'Area (Square Kilometers)',
          default: false
        },
        {
          name: 'areasqmi',
          type: WorkflowComponentParamType.Boolean,
          title: 'Area (Square Miles)',
          default: false
        },
        {
          name: 'brpoints',
          type: WorkflowComponentParamType.Boolean,
          title: 'Bounding Rectangle as Points',
          default: false
        },
        {
          name: 'brpolyg',
          type: WorkflowComponentParamType.Boolean,
          title: 'Bounding Rectangle as Polygon',
          default: false
        },
        {
          name: 'brxy',
          type: WorkflowComponentParamType.Boolean,
          title: 'Bounding Rectangle as X and Y Fields',
          default: false
        },
        {
          name: 'centroid',
          type: WorkflowComponentParamType.Boolean,
          title: 'Centroid as Spatial Object',
          default: false
        },
        {
          name: 'centroidxy',
          type: WorkflowComponentParamType.Boolean,
          title: 'Centroid as X and Y Fields',
          default: false
        },
        {
          name: 'end',
          type: WorkflowComponentParamType.Boolean,
          title: 'End Points as Spatial Object',
          default: false
        },
        {
          name: 'endxy',
          type: WorkflowComponentParamType.Boolean,
          title: 'End Points as X and Y Fields',
          default: false
        },
        {
          name: 'lengthkm',
          type: WorkflowComponentParamType.Boolean,
          title: 'Length (Kilometers)',
          default: false
        },
        {
          name: 'lengthmi',
          type: WorkflowComponentParamType.Boolean,
          title: 'Length (Miles)',
          default: false
        },
        {
          name: 'numparts',
          type: WorkflowComponentParamType.Boolean,
          title: 'Number of Parts',
          default: false
        },
        {
          name: 'numpoints',
          type: WorkflowComponentParamType.Boolean,
          title: 'Number of Points',
          default: false
        },
        {
          name: 'spatialtype',
          type: WorkflowComponentParamType.Boolean,
          title: 'Spatial Object Type',
          default: false
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const columns: Array<string> = []
    if (params.areasqkm) {
      columns.push(
        `ST_AREA(${params.field}) * ${M_TO_KM} * ${M_TO_KM} AS area_kilometers`
      )
    }
    if (params.areasqmi) {
      columns.push(
        `ST_AREA(${params.field}) * ${M_TO_MILES} * ${M_TO_MILES} AS area_miles`
      )
    }
    if (params.brxy) {
      columns.push(`ST_BOUNDINGBOX(${params.field}).xmin AS br_Left,
      ST_BOUNDINGBOX(${params.field}).xmax AS br_right,
      ST_BOUNDINGBOX(${params.field}).ymin AS br_bottom,
      ST_BOUNDINGBOX(${params.field}).ymax AS br_top`)
    }
    if (params.brpoints) {
      columns.push(`ST_GEOGPOINT(ST_BOUNDINGBOX(${params.field}).xmin, ST_BOUNDINGBOX(${params.field}).ymin) AS br_min,
                    ST_GEOGPOINT(ST_BOUNDINGBOX(${params.field}).xmax, ST_BOUNDINGBOX(${params.field}).ymax) AS br_max`)
    }
    if (params.brpolyg) {
      columns.push(`ST_BOUNDINGBOX(${params.field}) AS bounding_rectangle`)
    }
    if (params.centroid) {
      columns.push(`ST_CENTROID(${params.field}) AS centroid`)
    }
    if (params.centroidxy) {
      columns.push(`ST_X(ST_CENTROID(${params.field})) AS centroid_x,
                    ST_Y(ST_CENTROID(${params.field})) AS centroid_y`)
    }
    if (params.end) {
      columns.push(`SAFE.ST_ENDPOINT(${params.field}) AS endpoint`)
    }
    if (params.endxy) {
      columns.push(`SAFE.ST_X(SAFE.ST_ENDPOINT(${params.field})) AS endpoint_x,
                    SAFE.ST_Y(SAFE.ST_ENDPOINT(${params.field})) AS endpoint_y`)
    }
    if (params.lengthkm) {
      columns.push(
        `IF(CONTAINS_SUBSTR(ST_GEOMETRYTYPE(${params.field}), 'Polygon'),
          ST_PERIMETER(${params.field}),
          ST_LENGTH(${params.field})
        ) * ${M_TO_KM} * ${M_TO_KM} AS length_kilometers`
      )
    }
    if (params.lengthmi) {
      columns.push(
        `IF(CONTAINS_SUBSTR(ST_GEOMETRYTYPE(${params.field}), 'Polygon'),
          ST_PERIMETER(${params.field}),
          ST_LENGTH(${params.field})
        ) * ${M_TO_MILES} * ${M_TO_MILES} AS length_miles`
      )
    }
    if (params.numparts) {
      columns.push(`ST_NUMGEOMETRIES(${params.field}) AS num_parts`)
    }
    if (params.numpoints) {
      columns.push(`ST_NUMPOINTS(${params.field}) AS num_points`)
    }
    if (params.numparts) {
      columns.push(`ST_GEOMETRYTYPE(${params.field}) AS spatial_object_type`)
    }
    return `SELECT ${columns.length ? columns.join(',\n') + ',\n' : ''} *
            FROM ${connection.quoteName(params.source as string)}`
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { SpatialInfo }
